import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/utils/classnames';

const buttonVariants = cva(
  `[&_svg:not([class*='size-'])]:h-4 [&_svg:not([class*='size-'])]:w-4 [&_svg]:shrink-0
   inline-flex items-center justify-center gap-1.5 whitespace-nowrap text-sm outline-none transition-[color,box-shadow]
   disabled:pointer-events-none disabled:opacity-50 cursor-pointer`,
  {
    variants: {
      variant: {
        default:
          'font-medium bg-gradient-to-b from-[#3935FF] to-[#8F00FF] text-white shadow-sm hover:opacity-90 transition-opacity duration-200',
        secondary:
          'font-medium bg-white text-fx-neutral-900 border border-fx-neutral-300 hover:bg-fx-brand-100 hover:border-fx-primary hover:shadow-selected',
        link: 'text-fx-primary leading-5 font-normal !p-0 underline-offset-4 hover:underline',
        text: '!inline !p-0 !h-auto !text-[length:inherit] !text-[color:inherit] !font-inherit !leading-inherit hover:!text-fx-primary transition-colors underline',
        toast: 'hover:bg-fx-neutral-900/25 text-white',
        icon: 'hover:bg-fx-brand-100 rounded-lg',
        ghost:
          'font-medium bg-transparent hover:bg-fx-neutral-100 text-fx-neutral-900 shadow-none',
        outline:
          'font-medium border border-fx-neutral-300 bg-transparent hover:bg-fx-neutral-100 text-fx-neutral-900 disabled:bg-fx-neutral-50',
      },
      size: {
        default: 'has-[>svg]:px-3 h-9 rounded-lg px-4 py-2',
        sm: 'gap-2 rounded-lg px-3 py-1.5',
        icon: 'h-9 w-9',
        link: 'h-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
